import React, { useEffect, useMemo, useState } from 'react';

import { faClose, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as LockSVG } from 'assets/svg/lock.svg';
import { ReactComponent as UnlockSVG } from 'assets/svg/unlocked.svg';
import { ButtonSizeVariants, ButtonVariants } from 'constants/common';
import {
  FILTER_CONDITIONS_FOR_BOOLEANS,
  FILTER_CONDITIONS_FOR_NEW_FILTER,
  FILTER_CONDITIONS_FOR_NUMBERS,
  FILTER_CONDITIONS_FOR_STRINGS,
  FilterItem,
} from 'constants/reports-templates';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import { Input, SelectFormInput, Button, InputNumberFormat } from 'shared-components';
import DatePickerInput from 'shared-components/DatePickerInput';
import ModalControlButtons from 'shared-components/ModalControlButtons';

type Props = {
  selectedColumnsData?: ReportingColumn[];
  control: any;
  selectedTable: string | null;
  onCloseModal: VoidFunction;
  onSubmit: any;
  onDeleteFilter?: () => void;
  initialFilters?: FilterItem[];
};

const CreateReportTemplateFiltersModal: React.FC<Props> = ({
  selectedColumnsData = [],
  control,
  selectedTable,
  onCloseModal,
  onSubmit,
  initialFilters = [],
}) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();

  const createEmptyFilter = (): FilterItem => ({
    id: crypto.randomUUID(),
    column: null,
    condition: null,
    value: '',
    locked: false,
    operator: 'And',
  });

  const [filters, setFilters] = useState<FilterItem[]>(() =>
    initialFilters && initialFilters.length
      ? initialFilters.map((f) => ({ ...f, id: f.id ?? crypto.randomUUID() }))
      : [createEmptyFilter()]
  );
  const watchedValues = useWatch({ control });

  const getConditionsByType = (type?: number) => {
    switch (type) {
      case 5:
        return FILTER_CONDITIONS_FOR_NUMBERS;
      case 4:
        return FILTER_CONDITIONS_FOR_BOOLEANS;
      case 3:
        return FILTER_CONDITIONS_FOR_NUMBERS;
      case 2:
        return FILTER_CONDITIONS_FOR_STRINGS;
      default:
        return [];
    }
  };

  const availableColumns = useMemo(() => {
    return selectedColumnsData.filter((col) => col.tableName === selectedTable);
  }, [selectedColumnsData, selectedTable]);

  const updateFilter = (id: string | number, updates: Partial<FilterItem>) => {
    setFilters((prev) =>
      prev.map((filter) => {
        if (String(filter.id) === String(id)) {
          const hasActualChanges = Object.keys(updates).some(
            (key) => String(updates[key as keyof FilterItem]) !== String(filter[key as keyof FilterItem])
          );
          return hasActualChanges ? { ...filter, ...updates } : filter;
        }
        return filter;
      })
    );
  };

  const isSaveDisabled = filters.some((filter) => !filter.column || filter.condition === null || !filter.value);

  const resolveColumn = (colName?: string) =>
    selectedColumnsData.find((c) => c.name === colName && c.tableName === selectedTable);

  const renderValueInput = (filter: FilterItem) => {
    const column = resolveColumn(filter.column as string);

    switch (column?.columnType) {
      case 5:
        return (
          <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <DatePickerInput
              className="w-full"
              name={`dateValue-${filter.id}`}
              control={control}
              inputClassName="w-48"
            />
          </div>
        );
      case 4:
        return (
          <SelectFormInput
            control={control}
            name={`boolValue-${filter.id}`}
            value={filter.value ?? ''}
            options={[
              { label: 'true', value: 'true' },
              { label: 'false', value: 'false' },
            ]}
            onSelectValue={(opt) => updateFilter(filter.id, { value: opt?.value ?? '' })}
          />
        );
      case 3:
        return (
          <InputNumberFormat
            name={`numberValue-${filter.id}`}
            control={control}
            value={filter.value ?? ''}
            isShownArrowButtons
            fixedDecimalLength={0}
            inputClassName="w-40"
            onChange={(value) => updateFilter(filter.id, { value: value ?? '' })}
          />
        );
      case 2:
        return (
          <Input
            name={`stringValue-${filter.id}`}
            value={filter.value ?? ''}
            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
            inputClassName="!w-48 !text-black-900 !dark:text-white-00 !text-bodyMd"
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!initialFilters || initialFilters.length === 0) {
      return;
    }

    const newFilters = initialFilters.map((filter) => ({
      ...filter,
      id: filter.id ?? crypto.randomUUID(),
    }));

    setFilters(newFilters);
  }, [initialFilters]);

  useEffect(() => {
    if (!filters.length || !selectedColumnsData.length) return;

    setTimeout(() => {
      filters.forEach((filter, index) => {
        const column = selectedColumnsData.find((col) => {
          const nameMatch = col.name === filter.column || col.displayName === filter.column;
          const tableMatch = !selectedTable || col.tableName === selectedTable;
          return nameMatch && tableMatch;
        });

        if (filter.column && column) {
          setValue(`column-${filter.id}`, {
            label: filter.column,
            value: filter.column,
          });
        }

        if (filter.condition !== null && column) {
          const conditionList = getConditionsByType(column.columnType);
          const selectedCondition = conditionList.find((cond) => cond.id === filter.condition);

          if (selectedCondition) {
            setValue(`condition-${filter.id}`, {
              label: selectedCondition.label,
              value: String(selectedCondition.id),
            });
          }
        }

        if (filter.operator && index > 0) {
          setValue(`operator-${filter.id}`, {
            label: filter.operator,
            value: filter.operator,
          });
        }

        if (column) {
          switch (column.columnType) {
            case 3:
              break;
            case 5:
              setValue(`dateValue-${filter.id}`, filter.value);
              break;
            case 4:
              setValue(`boolValue-${filter.id}`, filter.value);
              break;
            case 2:
              setValue(`stringValue-${filter.id}`, filter.value);
              break;
          }
        }
      });
    }, 300);
  }, [filters, selectedColumnsData, selectedTable, setValue]);

  useEffect(() => {
    localStorage.setItem('filter', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    filters.forEach((filter) => {
      const column = selectedColumnsData.find((col) => col.name === filter.column);
      if (column?.columnType === 3) {
        const fieldName = `numberValue-${filter.id}`;
        const newValue = watchedValues?.[fieldName];
        if (newValue !== undefined && String(newValue) !== String(filter.value)) {
          updateFilter(filter.id, { value: newValue });
        }
      }
    });
  }, [watchedValues, filters, selectedColumnsData]);

  return (
    <div className="flex flex-col gap-3 w-[40rem]">
      {filters.map((filter, index) => (
        <div key={filter.id} className="flex gap-2 items-center w-full">
          <div
            className="min-w-[2.5rem] h-10 flex items-center justify-center border border-neutral-200 dark:border-neutral-800 rounded-md shrink-0 cursor-pointer"
            data-tooltip-id={`tooltip-${filter.id}`}
            onClick={() => updateFilter(filter.id, { locked: !filter.locked })}
          >
            {filter.locked ? <LockSVG className="w-4 h-4" /> : <UnlockSVG className="w-4 h-4" />}
            <Tooltip
              id={`tooltip-${filter.id}`}
              place="top"
              positionStrategy="fixed"
              className="tooltip-styles hidden lg:block !bg-black !opacity-100"
            >
              {filter.locked ? 'Locked filter' : 'Unlocked filter'}
            </Tooltip>
          </div>

          {index > 0 && (
            <SelectFormInput
              control={control}
              name={`operator-${filter.id}`}
              options={FILTER_CONDITIONS_FOR_NEW_FILTER}
              value={filter.operator}
              inputClassName="w-full capitalize"
              onSelectValue={(opt) => updateFilter(filter.id, { operator: opt?.value as 'And' | 'Or' })}
            />
          )}

          <SelectFormInput
            control={control}
            name={`column-${filter.id}`}
            inputClassName="!h-10"
            placeholder="Select column"
            options={availableColumns.map((col) => ({
              label: col.name,
              value: col.name,
            }))}
            onSelectValue={(opt) =>
              updateFilter(filter.id, {
                column: opt?.value ?? null,
                condition: null,
                value: '',
              })
            }
            value={filter.column ?? undefined}
          />

          {filter.column && (
            <SelectFormInput
              control={control}
              name={`condition-${filter.id}`}
              placeholder="Select condition"
              options={getConditionsByType(selectedColumnsData.find((c) => c.name === filter.column)?.columnType).map(
                (cond) => ({ value: String(cond.id), label: cond.label })
              )}
              onSelectValue={(opt) => {
                const condition = Number(opt?.value);
                if (!Number.isNaN(condition)) {
                  updateFilter(filter.id, { condition, value: '' });
                }
              }}
              value={filter.condition !== null ? String(filter.condition) : undefined}
            />
          )}

          {filter.column && filter.condition !== null && renderValueInput(filter)}

          <FontAwesomeIcon
            icon={faClose}
            onClick={() =>
              setFilters((prev) =>
                prev.length === 1
                  ? [
                      {
                        id: crypto.randomUUID(),
                        column: null,
                        condition: null,
                        value: '',
                        locked: false,
                        operator: 'And',
                      },
                    ]
                  : prev.filter((f) => f.id !== filter.id)
              )
            }
            className="text-neutral-500 w-4 h-4 cursor-pointer"
          />
        </div>
      ))}

      <Button
        className="w-28"
        type="button"
        onClick={() =>
          setFilters((prev) => [
            ...prev,
            { id: crypto.randomUUID(), column: null, condition: null, value: '', locked: false, operator: 'And' },
          ])
        }
        variant={ButtonVariants.OUTLINE}
        size={ButtonSizeVariants.SM}
        disabled={isSaveDisabled}
      >
        <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-1" />
        {t('AddFilter')}
      </Button>

      <div className="flex gap-2 justify-end mt-4">
        <ModalControlButtons
          onClose={onCloseModal}
          onSubmit={() =>
            onSubmit(
              filters.map((filter) => ({
                column: filter.column,
                condition: filter.condition,
                value: filter.value,
                locked: filter.locked,
                operator: filter.operator,
              }))
            )
          }
          saveButtonText={t('Save')}
          disabled={isSaveDisabled}
        />
      </div>
    </div>
  );
};

export default CreateReportTemplateFiltersModal;
